<template>
  <v-app-bar absolute flat class="header" height="127">
    <v-container>
      <div class="d-flex justify-space-between align-center">
        <LogoButton to="/" class="mr-10" />
        <NameBlock :company="companyName" :name="staffName" v-if="!props.hideButtons && isLoggedin" />
        <div v-if="!props.hideButtons && isLoggedin" style="margin-left: 100px;">
          <HeaderButton
              styling="Cart"
              :count="cartItemCount"
              to="/cart"
              id="tour-01002-step9"
          />
        </div>

        <div v-if="!props.hideButtons && isLoggedin">
          <div class="mb-3 mr-2" style="text-align: center;">掲載情報最終更新日： {{ formatter.formatDate(lastUpdated, 'YYYY年MM月DD日 HH時mm分') }}</div>
          <div class="header-buttons">
            <div><HeaderButton
                    styling="MyPage"
                    to="/mypage" /></div>
            <div><HeaderButton
                    styling="Signout"
                    @click="doSignout" /></div>
          </div>
        </div>
      </div>
    </v-container>
    <template v-slot:extension>
      <Breadcrumbs :items="props.breadcrumbItems" />
    </template>
  </v-app-bar>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, watchEffect, toValue } from 'vue'
import Breadcrumbs from '@/components/molecules/global/Breadcrumbs.vue'
import HeaderButton from '@/components/molecules/buttons/HeaderButton.vue'
import LogoButton from '@/components/molecules/buttons/LogoButton.vue'
import { BreadcrumbItemType } from '@/utils/breadcrumbs'
import { useAuthFunc } from '@/utils/auth'
import NameBlock from '@/components/molecules/blocks/NameBlock.vue'
import store from '@/store'
import { useSearchCartItemsFunc } from '@/services/searchCartItems'
import { useGetLastUpdatedFunc } from '@/services/getLastUpdated'
import { useRoute } from 'vue-router'
import { useFormatFunc } from '@/utils/formatter'
import { useGtm } from '@gtm-support/vue-gtm'

const route = useRoute()
const authUtil = useAuthFunc()
const formatter = useFormatFunc()

interface Props {
  breadcrumbItems?: BreadcrumbItemType[] | undefined,
  hideButtons?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  breadcrumbItems: undefined,
  hideButtons: false,
})

/**
 * 得意先名と担当者名
 */
const companyName = computed(() => store.getters['loginUser/staff']?.name ? store.getters['loginUser/staff'].name : '')
/**
 * 得意先名と担当者名
 */
const staffName = computed(() => store.getters['loginUser/customer']?.name ? store.getters['loginUser/customer'].name : '')
/**
 * カート内商品在庫の数
 */
const cartItemCount = computed(() => countCartItems.value)

/**
 * ログインしているかどうか
 */
const isLoggedin = computed(() => authUtil.isLoggedin())

/**
 * サインアウト
 */
const doSignout = () => {
  if ( authUtil.signout() ) {
    location.href = '/'
  }
}

// 最終更新日を取得 ---------
const lastUpdated = ref<Date | undefined>(undefined)
const {
  getLastUpdated,
  getLastUpdatedOnResult,
  getLastUpdatedOnError
} = useGetLastUpdatedFunc()

getLastUpdatedOnError((error) => {
  console.log(error.message)
})

getLastUpdatedOnResult((result) => {
  if (result.data && result.data.aggregateStockItem?._max?.updatedAt){
    lastUpdated.value = result.data.aggregateStockItem?._max.updatedAt
  }
})


// カート商品を取得 ---------
const countCartItems = ref<number>(0)
const {
  searchCartItemsCount,
  searchCartItemsCountOnResult,
  searchCartItemsCountOnError,
} = useSearchCartItemsFunc()

searchCartItemsCountOnError((error) => {
  console.log(error.message)
})

searchCartItemsCountOnResult((result) => {
  if (result.data && result.data?.aggregateCartItem?._count){
    countCartItems.value = result.data?.aggregateCartItem?._count._all
  }
})

onMounted(() => {
  searchCartItemsCount({})
  getLastUpdated()
})

watch(store.state.cart, (value) => {
  searchCartItemsCount({})
  getLastUpdated()
})

// route変更時の処理
watch(route, () => {
  searchCartItemsCount({})
  getLastUpdated()
})

const gtm = useGtm()

watchEffect(() => {
  if (gtm?.enabled() && toValue(companyName) && toValue(staffName)) {
    window.dataLayer?.push({
      user_id: `${toValue(companyName)}:${toValue(staffName)}`,
      companyName: toValue(companyName),
      staffName: toValue(staffName),
    })
  }
})

</script>

<style lang="scss" scoped>
.header {
  &-buttons {
    display: flex;
    justify-content: end;
    column-gap: 20px;
    align-items: center;
  }
  &::v-deep(.v-toolbar__content) {
    background-color: rgb(var(--v-theme-green-02));
    box-sizing: border-box;
    border-radius: 0 0 50px 50px;
  }
}
.v-app-bar.v-toolbar {
  background-color: transparent;
}
</style>
